import { useEffect, useRef, useState } from 'react';
import type { ChatUser } from '../../shared/events';
import type { Strings } from '../lib/i18n';
import type { FeedRow } from '../lib/session';

const PALETTE = ['#4A3A2A', '#2A3A4A', '#3A2A3A', '#2A4A3A', '#4A2A33', '#2A2F4A', '#33402A', '#40302A'];

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function initials(user: ChatUser): string {
  const n = user.nickname || user.uniqueId;
  if (n.startsWith('+')) return n;
  const parts = n.replace(/[^\p{L}\p{N} ._]/gu, '').split(/[\s._]+/).filter(Boolean);
  const s = parts.length >= 2 ? parts[0][0] + parts[1][0] : n.slice(0, 2);
  return s.toUpperCase();
}

export function Avatar({ user, size = '' }: { user: ChatUser; size?: '' | 'lg' | 'xl'; ring?: boolean }) {
  const [broken, setBroken] = useState(false);
  const bg = PALETTE[hash(user.uniqueId || user.nickname) % PALETTE.length];
  const cls = `avatar${size ? ` avatar--${size}` : ''}`;
  if (user.avatar && !broken) {
    return (
      <span className={cls} style={{ background: bg }}>
        <img src={user.avatar} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} />
      </span>
    );
  }
  return (
    <span className={cls} style={{ background: bg }}>
      {initials(user)}
    </span>
  );
}

export const TAG_COLOR: Record<FeedRow['kind'], string | null> = {
  chat: null,
  gift: '#FFB23F',
  follow: '#5BE3B0',
  share: '#7CC0FF',
  like: '#FF8F85',
  join: '#A8A8B3',
};

export function tagLabel(kind: FeedRow['kind'], t: Strings): string {
  switch (kind) {
    case 'gift':
      return t.tagGift;
    case 'follow':
      return t.tagFollow;
    case 'share':
      return t.tagShare;
    case 'like':
      return t.tagLike;
    case 'join':
      return t.tagJoin;
    default:
      return '';
  }
}

export function EventRow({ row, t, fontSize, reading }: { row: FeedRow; t: Strings; fontSize: number; reading?: boolean }) {
  const color = TAG_COLOR[row.kind];
  return (
    <div className={`ev${reading ? ' ev--reading' : ''}`}>
      <Avatar user={row.user} />
      <div className="ev__body">
        <div className="ev__head">
          <span className="ev__name">{row.user.nickname || row.user.uniqueId}</span>
          {color && (
            <span className="tag" style={{ background: color }}>
              {tagLabel(row.kind, t)}
            </span>
          )}
        </div>
        <span className={`ev__text${row.kind === 'chat' ? '' : ' ev__text--soft'}`} style={{ fontSize }}>
          {row.text}
          {row.image && <img className="ev__gift" src={row.image} alt="" referrerPolicy="no-referrer" />}
        </span>
      </div>
    </div>
  );
}

export type FeedFilter = 'all' | 'chat' | 'gift' | 'follow';

const GROUPS: Record<FeedFilter, FeedRow['kind'][] | null> = {
  all: null,
  chat: ['chat'],
  gift: ['gift', 'like'],
  follow: ['follow', 'share', 'join'],
};

export function filterRows(rows: FeedRow[], f: FeedFilter): FeedRow[] {
  const g = GROUPS[f];
  return g ? rows.filter((r) => g.includes(r.kind)) : rows;
}

/** Scrollable feed that sticks to the bottom unless the user scrolled up. */
export function Feed({ rows, t, fontSize, currentId, empty }: { rows: FeedRow[]; t: Strings; fontSize: number; currentId?: string | null; empty: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  useEffect(() => {
    const el = ref.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [rows]);
  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };
  return (
    <div className="feed" ref={ref} onScroll={onScroll}>
      {rows.length === 0 && <div className="feed__empty">{empty}</div>}
      {rows.map((r) => (
        <EventRow key={r.id} row={r} t={t} fontSize={fontSize} reading={r.id === currentId} />
      ))}
    </div>
  );
}
